import { Loader2, Upload } from "lucide-react"
import { type ChangeEvent, useCallback, useState } from "react"

import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"

async function autocropImage(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(blob)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)

      const canvas = document.createElement("canvas")
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext("2d")
      if (!ctx) {
        resolve(blob)
        return
      }

      ctx.drawImage(img, 0, 0)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const { data, width, height } = imageData

      let minX = width
      let minY = height
      let maxX = 0
      let maxY = 0

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const alpha = data[(y * width + x) * 4 + 3]
          if (alpha > 10) {
            minX = Math.min(minX, x)
            minY = Math.min(minY, y)
            maxX = Math.max(maxX, x)
            maxY = Math.max(maxY, y)
          }
        }
      }

      if (minX >= maxX || minY >= maxY) {
        resolve(blob)
        return
      }

      const padding = 4
      minX = Math.max(0, minX - padding)
      minY = Math.max(0, minY - padding)
      maxX = Math.min(width - 1, maxX + padding)
      maxY = Math.min(height - 1, maxY + padding)

      const croppedWidth = maxX - minX + 1
      const croppedHeight = maxY - minY + 1

      const croppedCanvas = document.createElement("canvas")
      croppedCanvas.width = croppedWidth
      croppedCanvas.height = croppedHeight
      const croppedCtx = croppedCanvas.getContext("2d")
      if (!croppedCtx) {
        resolve(blob)
        return
      }

      croppedCtx.drawImage(
        canvas,
        minX,
        minY,
        croppedWidth,
        croppedHeight,
        0,
        0,
        croppedWidth,
        croppedHeight,
      )

      croppedCanvas.toBlob(
        (croppedBlob) => {
          resolve(croppedBlob ?? blob)
        },
        "image/png",
        1,
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error("Failed to load image"))
    }
    img.src = objectUrl
  })
}

interface SavedSignature {
  id: number
  label: string | null
  url: string
}

interface SignatureProviderProps {
  savedSignatures: SavedSignature[]
  onSignatureReady: (dataUrl: string, bytes: Uint8Array) => void
}

export function SignatureProvider({
  savedSignatures,
  onSignatureReady,
}: SignatureProviderProps) {
  const [processing, setProcessing] = useState(false)
  const [skipBgRemoval, setSkipBgRemoval] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const processImage = useCallback(
    async (blob: Blob) => {
      setProcessing(true)
      setError(null)

      try {
        let resultBlob: Blob

        if (skipBgRemoval) {
          resultBlob = blob
        } else {
          try {
            const { removeBackground } =
              await import("@imgly/background-removal")
            const bgRemovedBlob = await removeBackground(blob, {
              output: { format: "image/png" },
            })
            resultBlob = await autocropImage(bgRemovedBlob)
          } catch {
            setError("Background removal failed. Using original image.")
            resultBlob = blob
          }
        }

        const arrayBuffer = await resultBlob.arrayBuffer()
        const bytes = new Uint8Array(arrayBuffer)

        const reader = new FileReader()
        reader.onload = () => {
          onSignatureReady(reader.result as string, bytes)
        }
        reader.readAsDataURL(resultBlob)
      } catch {
        setError("Failed to process signature image.")
      } finally {
        setProcessing(false)
      }
    },
    [skipBgRemoval, onSignatureReady],
  )

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      if (!file.type.startsWith("image/")) {
        setError("Please upload a JPG or PNG image.")
        return
      }

      void processImage(file)
    },
    [processImage],
  )

  const handleSavedSignatureClick = useCallback(
    async (signature: SavedSignature) => {
      setProcessing(true)
      setError(null)

      try {
        const response = await fetch(signature.url)
        const blob = await response.blob()
        const arrayBuffer = await blob.arrayBuffer()
        const bytes = new Uint8Array(arrayBuffer)

        const reader = new FileReader()
        reader.onload = () => {
          onSignatureReady(reader.result as string, bytes)
          setProcessing(false)
        }
        reader.readAsDataURL(blob)
      } catch {
        setError("Failed to load saved signature.")
        setProcessing(false)
      }
    },
    [onSignatureReady],
  )

  return (
    <div className="space-y-4">
      {savedSignatures.length > 0 && (
        <div>
          <Label className="text-sm font-medium">Saved Signatures</Label>
          <div className="mt-2 flex flex-wrap gap-3">
            {savedSignatures.map((sig) => (
              <button
                key={sig.id}
                className="rounded-lg border p-2 transition-shadow hover:shadow-md"
                onClick={() => void handleSavedSignatureClick(sig)}
                disabled={processing}
              >
                <img
                  src={sig.url}
                  alt={sig.label ?? "Saved signature"}
                  className="h-12 w-auto object-contain"
                />
                {sig.label && (
                  <p className="text-muted-foreground mt-1 text-xs">
                    {sig.label}
                  </p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="signature-upload" className="text-sm font-medium">
          Upload Signature Image
        </Label>
        <p className="text-muted-foreground mt-1 text-sm">
          {skipBgRemoval
            ? "Your image will be used as-is — best for images with a transparent background."
            : "Took a photo of your signature on paper? No problem — we'll automatically remove the background."}
        </p>

        <label
          htmlFor="skip-bg-removal"
          className="bg-muted/50 mt-3 flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors select-none"
        >
          <Checkbox
            id="skip-bg-removal"
            checked={skipBgRemoval}
            onCheckedChange={(checked) => setSkipBgRemoval(checked === true)}
            className="mt-0.5"
          />
          <div>
            <span className="text-sm font-medium">
              My image already has a transparent background
            </span>
            <p className="text-muted-foreground mt-0.5 text-xs">
              Skip automatic background removal
            </p>
          </div>
        </label>

        <div className="mt-3">
          <label
            htmlFor="signature-upload"
            className="border-muted-foreground/25 hover:border-primary/50 flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed p-4 transition-colors"
          >
            {processing ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">
                  {skipBgRemoval
                    ? "Loading signature..."
                    : "Removing background..."}
                </span>
              </>
            ) : (
              <>
                <Upload className="text-muted-foreground h-5 w-5" />
                <span className="text-sm">
                  Upload a JPG or PNG of your signature
                </span>
              </>
            )}
            <input
              id="signature-upload"
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handleFileChange}
              disabled={processing}
            />
          </label>
        </div>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  )
}
